/**
 * Image Preloader Service
 * Handles dynamic preloading of critical images to improve LCP performance
 */

class ImagePreloader {
  constructor() {
    this.preloadedImages = new Set();
    this.preloadCache = new Map();
  }

  /**
   * Preload an image and return a promise
   * @param {string} src - Image source URL
   * @param {Object} options - Preload options
   * @returns {Promise<HTMLImageElement>}
   */
  preloadImage(src, options = {}) {
    const { crossOrigin = 'anonymous', priority = 'high' } = options;
    
    if (this.preloadCache.has(src)) {
      return this.preloadCache.get(src);
    }

    const promise = new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        this.preloadedImages.add(src);
        resolve(img);
      };
      
      img.onerror = (error) => {
        console.warn(`Failed to preload image: ${src}`, error);
        reject(error);
      };
      
      if (crossOrigin) {
        img.crossOrigin = crossOrigin;
      }
      
      img.src = src;
    });

    this.preloadCache.set(src, promise);
    return promise;
  }

  /**
   * Preload Firebase Storage agent logo
   * @param {string} agentId - Agent ID
   * @returns {Promise<HTMLImageElement>}
   */
  async preloadAgentLogo(agentId) {
    if (!agentId) return null;

    // Try to preload the most likely logo formats
    const baseUrl = `https://firebasestorage.googleapis.com/v0/b/discoveryworks-87513.appspot.com/o/agents%2F${agentId}%2Fbranding%2F`;
    const commonFormats = ['png', 'jpg', 'jpeg', 'webp'];
    
    for (const format of commonFormats) {
      try {
        const logoUrl = `${baseUrl}logo_${Date.now()}.${format}?alt=media`;
        await this.preloadImage(logoUrl, { priority: 'high' });
        return logoUrl;
      } catch (error) {
        // Continue to next format
        continue;
      }
    }
    
    return null;
  }

  /**
   * Add resource hints to document head
   * @param {string} domain - Domain to add hints for
   */
  addResourceHints(domain) {
    if (typeof document === 'undefined') return;

    const head = document.head;
    
    // DNS prefetch
    if (!document.querySelector(`link[rel="dns-prefetch"][href*="${domain}"]`)) {
      const dnsPrefetch = document.createElement('link');
      dnsPrefetch.rel = 'dns-prefetch';
      dnsPrefetch.href = `//${domain}`;
      head.appendChild(dnsPrefetch);
    }

    // Preconnect
    if (!document.querySelector(`link[rel="preconnect"][href*="${domain}"]`)) {
      const preconnect = document.createElement('link');
      preconnect.rel = 'preconnect';
      preconnect.href = `https://${domain}`;
      preconnect.crossOrigin = 'anonymous';
      head.appendChild(preconnect);
    }
  }

  /**
   * Create and inject preload link
   * @param {string} href - Resource URL
   * @param {string} as - Resource type
   * @param {Object} options - Additional options
   */
  injectPreloadLink(href, as = 'image', options = {}) {
    if (typeof document === 'undefined') return;

    const existing = document.querySelector(`link[rel="preload"][href="${href}"]`);
    if (existing) return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = as;
    
    if (options.crossOrigin) {
      link.crossOrigin = options.crossOrigin;
    }
    
    if (options.type) {
      link.type = options.type;
    }

    document.head.appendChild(link);
  }

  /**
   * Preload critical images based on route context
   * @param {string} route - Current route
   * @param {Object} context - Additional context (agentId, etc.)
   */
  preloadCriticalImages(route, context = {}) {
    const { agentId } = context;

    // Add Firebase Storage resource hints
    this.addResourceHints('firebasestorage.googleapis.com');

    // Route-specific preloading
    if (route.includes('/chat') || route.includes('/agent-preview')) {
      if (agentId) {
        this.preloadAgentLogo(agentId).catch(console.warn);
      }
    }
  }

  /**
   * Check if an image is already preloaded
   * @param {string} src - Image source URL
   * @returns {boolean}
   */
  isPreloaded(src) {
    return this.preloadedImages.has(src);
  }

  /**
   * Clear preload cache
   */
  clearCache() {
    this.preloadedImages.clear();
    this.preloadCache.clear();
  }
}

// Create singleton instance
const imagePreloader = new ImagePreloader();

export default imagePreloader; 